import React from 'react';
import cx from 'classnames';
import T from 'i18n-react';
import { useSessionStorage } from 'usehooks-ts';

import { LabelContainer } from '@motorway/motorway-component-library';
import { Badge } from '@motorway/mw-highway-code';
import { Toggle } from '@motorway/mw-highway-code/alpha';
import { VARIANT } from '@motorway/mw-highway-code/enums';

import ExpandableFilter from 'Components/shared/ExpandableFilter/ExpandableFilter';
import texts from 'Components/texts.json';
import useFilterView from 'Components/VehicleList/filters/useFilterView';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { triggerSeenFilterClickEvent } from 'Services/analytics/events';
import { useSeenFilterExperimentation } from 'Services/optimizely/experimentations/useSeenFilterExperimentation';
import { type ViewingFilterValue, VIEWING_FILTER } from 'Utilities/seenVehicles/seenVehicles';

import styles from './SeenFilter.module.scss';

T.setTexts(texts);

export const useSeenFilter = () =>
	useSessionStorage<ViewingFilterValue>('seen_filter', VIEWING_FILTER.ALL, {
		deserializer: (value) => {
			if (value === VIEWING_FILTER.NEW || value === VIEWING_FILTER.SEEN) return value;
			return VIEWING_FILTER.ALL;
		},
		serializer: (value) => value,
	});

const SeenFilterToggle: React.FC = () => {
	const [value, setValue] = useSeenFilter();
	const isChecked = value === VIEWING_FILTER.NEW;

	return (
		<div className={styles.toggleWrapper}>
			<Toggle
				showLabel
				formProps={{
					input: {
						checked: isChecked,
						name: 'seen-filter-toggle',
						onChange: () => {
							const nextValue = !isChecked;
							setValue(nextValue ? VIEWING_FILTER.NEW : VIEWING_FILTER.ALL);
							triggerSeenFilterClickEvent(nextValue);
						},
						type: 'checkbox',
					},
				}}
				id="seen-filter-toggle"
				label={`${T.translate('vehicleList.filters.seen.label')}`}
			/>
		</div>
	);
};

const renderSeenFilterTitle = () => {
	return (
		<div className={styles.titleWrapper}>
			{T.translate('vehicleList.filters.seen.title')}
			<Badge text={T.translate('vehicleList.filters.seen.new')} variant={VARIANT.PRIMARY} />
		</div>
	);
};

export const SeenFilter: React.FC = () => {
	const isSeenFilterEnabled = useSeenFilterExperimentation();
	const { isExpandableFilter } = useFilterView();
	const [value] = useSeenFilter();
	const {
		maxWidth: { breakpointMobile: isMobile },
	} = useBreakpoint();
	const isDirty = value !== VIEWING_FILTER.ALL;

	if (!isSeenFilterEnabled) return null;
	return (
		<>
			{isExpandableFilter ? (
				<ExpandableFilter
					classNames={{
						content: styles.expandableContent,
						expanded: styles.expandedContent,
					}}
					id="seenFilter"
					isDirty={isDirty}
					title={renderSeenFilterTitle()}
				>
					{/* @ts-expect-error ExpandableFilter uses render prop pattern */}
					{() => <SeenFilterToggle />}
				</ExpandableFilter>
			) : (
				<div className="mw-mb-3 mw-mt-3">
					<LabelContainer
						className={cx('mw-mt-3', { 'mw-mb-2': isMobile, 'mw-mb-3': !isMobile })}
						label={renderSeenFilterTitle()}
					/>
					<SeenFilterToggle />
				</div>
			)}
		</>
	);
};
